import Immutable from "immutable";
import round from "lodash/round";
import { stripCommas } from "lib/utils/numberFunctions";

import types, { DONATION_AMOUNT } from "./types";

import { RESET_GIVING_FORM } from "../types";

export const initialState = Immutable.fromJS({
  allocations: [],
  allocationIds: Immutable.Set(),
  selectedAllocations: [],
  isLoading: false,
  query: "",
  error: null,
  totalAmount: DONATION_AMOUNT,
  feeAmount: 0,
  donationId: null,
  formSubmitting: false,
  fetchedAllocations: false,
  selectedCategory: null,
  gatewayError: "",
  hasMounted: false,
  confirming: false,
  stripePaymentId: "",
  connectedStripeAccountId: "",
  stripePaymentIntentId: "",
  stripeClientSecretKey: "",
  donationToken: "",
  stripePublishableKey: "",
  stripeObject: null,
  stripeElementsObject: null,
  amountWithGiftAid: "",
});

export default (state = initialState, action) => {
  const { type } = action;

  switch (type) {
    case types.RECEIVED_CENTRALIZED_GIFTS: {
      if (Array.isArray(action.centralizedGifts)) {
        const formattedGifts = Immutable.fromJS(action.centralizedGifts);

        return state.withMutations((state) => {
          state.set("centralizedGifts", formattedGifts);
        });
      }

      return state;
    }

    case types.RECEIVED_ALLOCATIONS: {
      const formattedItems = Immutable.fromJS(
        action.allocations.map((allocation) => {
          allocation.amount = 0;
          return allocation;
        }),
      );

      const filteredAllocations = formattedItems.filter((item) => {
        return !state.get("allocationIds").includes(item.get("id"));
      });

      const newIds = state
        .get("allocationIds")
        .union(filteredAllocations.map((a) => a.get("id")));
      const newAllocations = state.get("allocations").concat(filteredAllocations);

      return state.withMutations((state) => {
        state
          .set("allocations", newAllocations)
          .set("allocationIds", newIds)
          .set("error", null);
      });
    }

    case types.MODAL_START_LOADER:
      return state.set("isLoading", true);

    case types.MODAL_DEFINITELY_STOP_LOADER:
      return state.set("isLoading", false);

    case types.FETCHED_ALLOCATIONS:
      return state.set("fetchedAllocations", true);

    case types.TRIGGER_IS_MOUNTED: {
      return state.set("hasMounted", true);
    }

    case types.SET_FEE_AMOUNT: {
      return state.set("feeAmount", action.feeAmount);
    }

    case types.DELETE_SELECTED_ALLOCATIONS: {
      return state.withMutations((state) => {
        state.set("selectedAllocations", state.get("selectedAllocations").clear());
      });
    }

    case types.SELECT_ALLOCATION:
      let updatedAfterSelect;

      if (action.bool === true) {
        // add new allocation (campaign)
        let allocation = action.allocation;
        const selectedAllocations = state.get("selectedAllocations");

        if (selectedAllocations.count() === 0) {
          allocation = allocation.set(
            "amount",
            parseFloat(`${action.totalAmount}`.replace(/,/g, "")),
          );
        }

        updatedAfterSelect = selectedAllocations.push(allocation);

        // Persistent Cart
        let persistentAmount = 0;
        if (window.PersistentCartService) {
          const currentId = allocation.get("id");
          const selectedAllocationIds = state
            .toJS()
            .selectedAllocations.map((selected) => selected.id);
          const alreadySelected = selectedAllocationIds.find(
            (element) => currentId === element,
          );

          if (
            window.PersistentCartService.isCampaignIdSelected(
              allocation.get("id") && alreadySelected,
            )
          ) {
            return state;
          }
          persistentAmount = allocation.get("amount") || 0;
          window.PersistentCartService.selectCampaign(allocation.toJS());
        }

        const addedBackAmount =
          round(parseFloat(state.get("totalAmount")), 2) + persistentAmount;
        return state.withMutations((state) => {
          state
            .set("totalAmount", addedBackAmount)
            .set("selectedAllocations", updatedAfterSelect);
        });
      }

      // remove allocation (campaign)
      // find the matching campaign
      let index;
      const selectedAllocation = state
        .get("selectedAllocations")
        .find((selectedAllocation, idx) => {
          if (selectedAllocation.get("id") === action.allocation.get("id")) {
            index = idx;
            return true;
          }
        });

      if (selectedAllocation) {
        const amountToRemove = action.allocation.get("amount") || 0;
        const addedBackAmount =
          round(parseFloat(state.get("totalAmount")), 2) - amountToRemove;
        updatedAfterSelect = state.get("selectedAllocations").delete(index);

        if (window.PersistentCartService) {
          window.PersistentCartService.deselectCampaign(action.allocation.toJS());
        }

        return state.withMutations((state) => {
          state
            .set("totalAmount", addedBackAmount)
            .set("selectedAllocations", updatedAfterSelect);
        });
      }

      throw new Error(`No allocation found with id ${action.allocation.get("id")}`);

    case types.SELECT_CART_ALLOCATIONS:
      const formattedItems = Immutable.fromJS(
        Object.values(window.PersistentCartService.selected()).map((allocation) => {
          allocation.amount = window.PersistentCartService.allocations()[allocation.id];
          return allocation;
        }),
      );

      const filteredAllocations = formattedItems.filter((item) => {
        return !state.get("allocationIds").includes(item.get("id"));
      });

      const newIds = state
        .get("allocationIds")
        .union(filteredAllocations.map((allocation) => allocation.get("id")));
      const newAllocations = state.get("allocations").concat(filteredAllocations);

      let selectedAllocations = state.get("selectedAllocations");
      newAllocations.forEach((allocation) => {
        selectedAllocations = selectedAllocations.push(allocation);
      });

      const totalAmount = Object.values(
        window.PersistentCartService.allocations(),
      ).reduce((total, amount) => {
        return parseFloat(total) + (parseFloat(amount) || 0);
      }, 0);

      return state.withMutations((newState) => {
        newState
          .set("allocations", newAllocations)
          .set("allocationIds", newIds)
          .set("error", null)
          .set("selectedAllocations", selectedAllocations)
          .set("totalAmount", `${totalAmount}`);
      });

    case types.MODAL_SET_AMOUNT:
      const indexOfAllocation = state
        .get("selectedAllocations")
        .indexOf(action.allocation);

      var amount = round(parseFloat(action.amount), 2);
      const previousAmount = action.allocation.get("amount") || 0;

      if (!Number.isNaN(amount)) {
        const updatedSelected = state
          .get("selectedAllocations")
          .update(indexOfAllocation, (allocation) => {
            return allocation.set("amount", amount);
          });

        const updatedAvailableAmount =
          (parseFloat(state.get("totalAmount")) || 0) + amount - previousAmount;

        if (window.PersistentCartService) {
          window.PersistentCartService.updateAllocation(
            action.allocation.get("id"),
            amount,
          );
        }

        return state.withMutations((state) => {
          state
            .set("selectedAllocations", updatedSelected)
            .set("totalAmount", updatedAvailableAmount);
        });
      }

      throw new Error(`${action.amount} is not a valid number`);

    case types.MODAL_SET_AMOUNT_FROM_FORM:
      var amount = parseFloat(stripCommas(action.amount));
      if (state.get("selectedAllocations").count() === 0) {
        return state.set("totalAmount", amount);
      } else if (state.get("selectedAllocations").count() === 1) {
        const updatedSelected = state
          .get("selectedAllocations")
          .update(0, (allocation) => {
            return allocation.set("amount", amount);
          });
        return state.withMutations((state) => {
          state.set("selectedAllocations", updatedSelected).set("totalAmount", amount);
        });
      }

    case types.MODAL_SET_NEW_AMOUNT:
      return state.set("totalAmount", action.amount);

    case types.MODAL_SET_QUERY: {
      if (typeof window.EventAnalyticsService != "undefined") {
        window.EventAnalyticsService.addEvent({
          name: "GIVING FORM - SEARCH",
          giving_form_version: "React",
        });
      }
      return state.set("query", action.query);
    }

    case types.MODAL_SET_TO_AVERAGE_DONATION:
      const causeCount = state.get("selectedAllocations").count();

      const updatedAmounts = state.get("selectedAllocations").map((cause) => {
        return cause.set("amount", action.amountPerCause);
      });

      return state.withMutations((state) => {
        state
          .set("totalAmount", action.amount)
          .set("selectedAllocations", updatedAmounts);
      });

    case types.SELECT_CATEGORY: {
      setTimeout(() => {
        if (typeof window.EventAnalyticsService == "undefined") return;
        window.EventAnalyticsService.addEvent({
          name: "GIVING FORM - SELECT CATEGORY",
          giving_form_version: "React",
          category: action.category,
        });
      }, 0);
      return state.set("selectedCategory", action.category);
    }

    case types.MODAL_SET_DONATION_ID:
      return state.set("donationId", action.id);

    case types.RESET_ALLOCATIONS:
      return state.withMutations((state) => {
        state.set("allocations", Immutable.List());
        state.set("allocationIds", Immutable.Set());
      });

    case types.SET_GATEWAY_ERROR: {
      return state.withMutations((state) => {
        state.set("gatewayError", action.err);
      });
    }
    case types.RESET_SUBMIT_ERROR:
      return state.withMutations((state) => {
        state.set("submitError", null);
      });
    case types.MODAL_SUBMITTING_FORM:
      return state.withMutations((state) => {
        state.set("formSubmitting", true);
        state.set("submitError", null);
        state.set("confirming", true);
      });
    case types.CONFIRMING_CHANGED:
      return state.withMutations((state) => {
        state.set("confirming", action.isConfirming);
      });
    case types.ADDING_GIFT_CHANGED:
      return state.withMutations((state) => {
        state.set("addingGift", action.isAddingGift);
      });
    case types.IS_GIFT_AID:
      return state.withMutations((state) => {
        state.set("isGiftAid", action.isGiftAid);
      });
    case types.STRIPE_PAYMENT_ID_CHANGED:
      return state.withMutations((state) => {
        state.set("stripePaymentId", action.id);
      });
    case types.STRIPE_CONNECTED_ACCOUNT_ID:
      return state.withMutations((state) => {
        state.set("connectedStripeAccountId", action.id);
      });
    case types.STRIPE_PAYMENT_INTENT_ID:
      return state.withMutations((state) => {
        state.set("stripePaymentIntentId", action.paymentIntentId);
      });
    case types.AMOUNT_WITH_GIFT_AID:
      return state.withMutations((state) => {
        state.set("amountWithGiftAid", action.amount);
      });
    case types.MODAL_SET_DONATION_TOKEN:
      return state.withMutations((state) => {
        state.set("donationToken", action.donationToken);
      });
    case types.STRIPE_CLIENT_SECRET_KEY:
      return state.withMutations((state) => {
        state.set("stripeClientSecretKey", action.id);
      });
    case types.STRIPE_PUBLISHABLE_KEY:
      return state.withMutations((state) => {
        state.set("stripePublishableKey", action.publishableKey);
      });
    case types.STRIPE_OBJECT:
      return state.withMutations((state) => {
        state.set("stripeObject", action.stripe);
      });
    case types.STRIPE_ELEMENTS_OBJECT:
      return state.withMutations((state) => {
        state.set("stripeElementsObject", action.elements);
      });
    case types.MODAL_FORM_SUBMITTED:
      if (!action.error && window.PersistentCartService) {
        window.PersistentCartService.clearStorage();
      }

      return state.withMutations((state) => {
        state.set("formSubmitting", false);
        state.set("submitError", action.error);
        if (action.error) {
          state.set("confirming", false);
        }
      });

    case types.SET_MINIMUM_VALUE_ERROR: {
      return state.set("minimumValueError", action.error);
    }
    case RESET_GIVING_FORM: {
      return initialState;
    }

    default:
      return state;
  }
};
